import React, { useEffect, useState, useCallback } from "react";
import { CreditCard, Check, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Price — Carte Stripe Connect pour le covoitureur.
 *
 * Affiche l'état du compte Stripe Connect du covoitureur et permet de
 * lancer / relancer l'onboarding. Une fois `charges_enabled=true`, les
 * courses payées par carte sont automatiquement virées sur son IBAN.
 *
 * Le lien d'onboarding est ouvert dans l'in-app browser (Capacitor
 * Browser) pour rester dans la même session que l'app.
 */
export default function StripeConnectCard() {
  const [status, setStatus] = useState({ connected: false, charges_enabled: false, payouts_enabled: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/price/driver/stripe/connect/status");
      setStatus(r.data || {});
    } catch (e) {
      // silent — non-blocking
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-check status when the user returns from the Stripe browser tab.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_connect") === "done") {
      refresh();
      // Clean the URL param.
      const url = new URL(window.location.href);
      url.searchParams.delete("stripe_connect");
      window.history.replaceState({}, "", url.toString());
    }
  }, [refresh]);

  const openOnboarding = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // On Capacitor native (iOS/Android), `window.location.origin` is
      // `capacitor://localhost` — an unusable scheme that Stripe rejects
      // when it tries to build a return URL. Fall back to the public
      // backend/preview URL in that case so the AccountLink lands on a
      // real HTTPS page.
      let origin = window.location.origin;
      if (!origin || !origin.toLowerCase().startsWith("https://")) {
        origin = process.env.REACT_APP_BACKEND_URL || "https://pricecovoit.fr";
      }
      const r = await api.post("/price/driver/stripe/connect/onboarding", {
        origin_url: origin,
      });
      const url = r.data?.url;
      if (!url) throw new Error("Lien Stripe indisponible");
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url });
      } catch {
        window.location.href = url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Erreur Stripe");
    } finally {
      setBusy(false);
    }
  };

  const ready = status.charges_enabled && status.payouts_enabled;

  // Carte masquée une fois le compte Stripe prêt — plus propre dans
  // le menu profil : le covoitureur n'a plus besoin de voir le
  // bouton "Terminer la configuration" une fois activé. Les
  // paiements partent de toute façon automatiquement.
  if (!loading && ready) return null;

  return (
    <div
      data-testid="stripe-connect-card"
      className={
        "rounded-2xl border-2 border-black p-4 shadow-[0_4px_0_0_#000] " +
        (ready ? "bg-[#D6F5D6]" : "bg-white")
      }
    >
      <div className="flex items-center gap-3">
        <div className={
          "w-10 h-10 rounded-xl border-2 border-black grid place-items-center shadow-[0_3px_0_0_#000] " +
          (ready ? "bg-[#2A9D3F]" : "bg-[#FF3B3B]")
        }>
          {ready ? (
            <Check className="w-5 h-5 text-white" strokeWidth={3} />
          ) : (
            <CreditCard className="w-5 h-5 text-white" strokeWidth={3} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[Outfit] text-[15px] font-black leading-tight">
            {ready ? "Paiements carte activés" : "Recevoir mes paiements"}
          </div>
          <div className="font-[Outfit] text-[11px] text-neutral-600 mt-0.5">
            {ready
              ? "Ton IBAN est lié — Stripe verse automatiquement ta part après chaque course carte."
              : "Configure ton compte Stripe pour recevoir ta part des courses payées par carte directement sur ton IBAN."}
          </div>
        </div>
      </div>
      {!loading && !ready && (
        <button
          type="button"
          data-testid="stripe-connect-open-onboarding"
          onClick={openOnboarding}
          disabled={busy}
          className="mt-3 w-full h-11 rounded-xl bg-[#FF3B3B] text-white font-[Outfit] font-black text-[14px] border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-60 disabled:active:translate-y-0 flex items-center justify-center gap-2"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <ExternalLink className="w-4 h-4" strokeWidth={3} />
              {status.connected ? "Terminer la configuration" : "Configurer maintenant"}
            </>
          )}
        </button>
      )}
    </div>
  );
}
